import { Checkbox, Form, Tooltip } from "antd"
import React, { useId } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import { PERMISSION_DEPENDENCIES } from "../../helpers/dependencies.ts"
import { getPermissionDomId, getPermissionLabel } from "../../helpers/layout.ts"
import { getDisplayEntry } from "../../helpers/utils.ts"
import type { AnyPermissionKey } from "../../types/permissions.types.ts"
import { usePermissionsEditor } from "./editor-context.ts"

interface PermissionCheckboxProps {
  sectionKey: string
  subKey: string
  actionKey: string
  initialValue: boolean
  // A grid cell has no visible label of its own, so it is named here.
  ariaLabel?: string
  // Off where the description is already printed beside the checkbox.
  describeInTooltip?: boolean
  children?: React.ReactNode
  className?: string
}

export const PermissionCheckbox = ({
  sectionKey,
  subKey,
  actionKey,
  initialValue,
  ariaLabel,
  describeInTooltip = true,
  children,
  className
}: PermissionCheckboxProps) => {
  const { t } = useTranslation()
  const descriptionId = useId()
  const { displayMap, highlightedKey } = usePermissionsEditor()

  const deps = PERMISSION_DEPENDENCIES[actionKey as AnyPermissionKey]
  const requires = deps?.length
    ? t("permissions.editor.requires", {
        names: deps.map((dep) => getPermissionLabel(dep, displayMap)).join(", ")
      })
    : undefined
  const description = describeInTooltip
    ? getDisplayEntry(displayMap, actionKey)?.tooltip
    : undefined
  const tooltip =
    description || requires ? (
      <>
        {description}
        {description && requires && <br />}
        {requires}
      </>
    ) : undefined

  return (
    <Tooltip title={tooltip} placement="top" mouseEnterDelay={0.3}>
      {/* span needed: Form.Item with noStyle doesn't forward hover events to Tooltip */}
      <span
        className={cn(
          "inline-flex rounded-md transition-shadow",
          highlightedKey === actionKey &&
            "ring-2 ring-primary ring-offset-2 ring-offset-white",
          className
        )}
      >
        <Form.Item
          name={[sectionKey, subKey, actionKey]}
          valuePropName="checked"
          initialValue={initialValue}
          noStyle
        >
          <Checkbox
            id={getPermissionDomId(actionKey)}
            aria-label={ariaLabel}
            aria-describedby={tooltip ? descriptionId : undefined}
            className="permission-checkbox"
          >
            {children}
          </Checkbox>
        </Form.Item>
        {tooltip && (
          <span id={descriptionId} className="sr-only">
            {description} {requires}
          </span>
        )}
      </span>
    </Tooltip>
  )
}
